import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import "./Pricing.css";

// Load Razorpay script
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};


const Pricing = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState({});
  const [currency, setCurrency] = useState("USD");
  const [rates, setRates] = useState({ USD: 1, INR: 83, EUR: 0.92, GBP: 0.79 });

  useEffect(() => {
    loadPlans();
    loadRates();
    detectCountry(); // <-- ONLY THING ADDED
  }, []);

  const detectCountry = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();

      if (data.country === "IN") {
        setCurrency("INR");
      }
    } catch (e) {
      console.log("Country detection failed");
    }
  };

  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API}/pricing/plans`);
      setPlans(res.data);
    } catch {}
  };

  // Fetch live USD → INR rate from backend
const loadRates = async () => {
  try {
    const r = await axios.get(`${API}/pricing/rate`);
    const rate = r.data.rate;

    setRates(prev => ({
      ...prev,
      INR: rate   // update only INR
    }));
  } catch (e) {
    console.log("Failed to load live rate");
  }
};


  // Correct USD → selected currency conversion
const convertPrice = (usdPrice) => {
  if (!rates || !rates[currency]) return usdPrice.toFixed(2);
  return (usdPrice * rates[currency]).toFixed(2);
};
  const symbol = { USD:"$", INR:"₹", EUR:"€", GBP:"£" }[currency];

  const subscribe = async (id) => {
  if (!user) return navigate("/signup?redirect=/pricing");
  // Allow upgrades: only block if buying same plan
if (user.is_premium && user.premium_type === id) {
  return toast.info("Already active plan!");
}

  setLoading({ [id]: true });

  // Load Razorpay script
  const ok = await loadRazorpay();
  if (!ok) {
    toast.error("Failed to load Razorpay");
    return setLoading({ [id]: false });
  }

  try {
    // 1️⃣ Create order on backend
    const token = localStorage.getItem("token");

const res = await axios.post(
  `${API}/payments/razorpay/order`,
  id,   // ✔️ SEND ONLY PLAIN STRING, NOT JSON OBJECT
  {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  }
);

    const { order_id, key, amount, currency } = res.data;

    // 2️⃣ Razorpay checkout setup
    const options = {
      key,
      amount,
      currency,
      name: "GrammAA Premium",
      description: "Upgrade to premium plan",
      image: "https://grammaa.com/icons/logo-512.png",
      order_id,
      handler: async function (response) {
        try {
          // 3️⃣ Verify payment on backend
          const verify = await axios.post(
  `${API}/payments/razorpay/verify`,
  {
    razorpay_order_id: response.razorpay_order_id,
    razorpay_payment_id: response.razorpay_payment_id,
    razorpay_signature: response.razorpay_signature
  },
  {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
);

          if (verify.data.status === "success") {
            toast.success("Payment successful!");
            navigate("/payment-success");
          } else {
            toast.error("Verification failed");
          }
        } catch {
          toast.error("Verification error");
        }
      },
      theme: { color: "#4F46E5" }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (err) {
    toast.error("Payment failed");
  }

  setLoading({ [id]: false });
};

  return (
    <div className="pricing-page pricing-container">
      <div className="glass-background"></div>

      {/* NAVBAR */}
      <nav className="pricing-nav fade-in">
        <div className="nav-brand" onClick={() => navigate("/")}>
          <img src="/icons/logo-512.png" className="nav-logo" />
          <span className="brand-text">GrammAA</span>
        </div>

        <div className="nav-links">
          {user ? (
            <>
              <Button variant="ghost" onClick={() => navigate("/dashboard")}>Dashboard</Button>
              <Button onClick={() => navigate("/checker")}>Checker</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")}>Login</Button>
              <Button onClick={() => navigate("/signup")}>Sign Up</Button>
            </>
          )}
        </div>
      </nav>

      {/* HEADER */}
      <div className="pricing-header fade-in">
        <h1>Choose Your Plan</h1>
        <p className="pricing-subtitle">
          Unlock unlimited grammar checking and premium features
        </p>
      </div>

      {/* PRICING GRID */}
      <div className="pricing-grid fade-in">

        {/* FREE PLAN */}
        <Card className="plan-card">
          <div className="plan-header">
            <h2 className="plan-name">Free</h2>

            <div className="plan-price">
              <span className="price-amount">Free</span>
              <span className="price-period">/forever</span>
            </div>
          </div>

          <ul className="plan-features">
            <li>✓ 3 grammar checks per day</li>
            <li>✓ Basic AI corrections</li>
            <li>✓ Grammar score</li>
            <li>✓ Mistake explanations</li>
            <li>✓ 5 rewrite styles</li>
          </ul>

          <Button
            variant="outline"
            className="plan-button"
            disabled={user && !user.is_premium}
            onClick={() => !user && navigate("/signup")}
          >
            {user?.is_premium ? "Premium Active" : user ? "Current Plan" : "Get Started"}
          </Button>
        </Card>

        {/* PREMIUM PLANS */}
        {plans.map((p) => (
          <Card
            key={p.id}
            className={`plan-card ${p.id === "yearly" ? "featured-plan" : ""}`}
          >
            {p.id === "yearly" && (
              <Badge className="featured-badge">Best Value</Badge>
            )}

            <div className="plan-header">
              <h2 className="plan-name">{p.name}</h2>

              <div className="plan-price">
                <span className="price-amount">
                  {symbol}{convertPrice(p.price)}
                </span>
                <span className="price-period">
                  /{p.id === "monthly" ? "month" : "year"}
                </span>
              </div>
            </div>

            <ul className="plan-features">
              {p.features.map((f, i) => (
                <li key={i}>✓ {f}</li>
              ))}
            </ul>

            <Button
  className="plan-button glass-btn"
  disabled={
    loading[p.id] ||
    (user?.is_premium && user?.premium_type === p.id) // disable only CURRENT plan
  }
  onClick={() => subscribe(p.id)}
>
  {
    loading[p.id]
      ? "Processing…"
      : (user?.is_premium && user?.premium_type === p.id)
        ? "Active"
        : "Subscribe Now"
  }
</Button>

          </Card>
        ))}
      </div>

      {/* FAQ */}
      <div className="pricing-faq fade-in">
        <h2>Frequently Asked Questions</h2>

        <div className="faq-grid">
          <div className="faq-item">
            <h3>What happens after I reach my daily limit?</h3>
            <p>Free users get 3 checks/day. Premium users get unlimited usage.</p>
          </div>

          <div className="faq-item">
            <h3>Can I cancel my subscription anytime?</h3>
            <p>You can cancel anytime. Access continues until the cycle ends.</p>
          </div>

          <div className="faq-item">
            <h3>What AI model do you use?</h3>
            <p>GrammAA uses GPT-5.1 for high-accuracy writing correction.</p>
          </div>

          <div className="faq-item">
            <h3>Is my text private?</h3>
            <p>Everything is encrypted, never stored, never shared.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
